import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { Button } from "@flat-ui";
import { assert } from "@faro-lotv/foundation";
import { useToast } from "@hooks/use-toast";
import { useAppParams } from "@router/router-helper";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useState } from "react";

/** Renders the section to get an export data token */
export function GetToken(): JSX.Element {
  const { companyId } = useAppParams();
  assert(companyId, "Company ID has to be defined");
  const { showToast } = useToast();
  const { handleErrorWithToast } = useErrorContext();
  const { trackEvent } = useTrackEvent();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /** Gets the token from the backend and downloads it as text file */
  const downloadCode = useCallback(async (): Promise<void> => {
    trackEvent({
      name: WorkspaceEvents.downloadCode,
    });

    setIsLoading(true);

    try {
      // Fetch the token as string from the backend
      const { token } =
        await faroCoreApiClient.company.getCompanyProjectExportToken({
          companyId,
        });

      // Create a Blob from the token string
      const blob = new Blob([token], { type: "text/plain" });

      // Create a link element, set its href to the Blob URL, and trigger a download
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "sphere-xg-download-code.txt";
      document.body.appendChild(link);
      link.click();

      // Track the successful event
      trackEvent({
        name: WorkspaceEvents.finishDownloadCode,
        props: {
          isSuccess: true,
        },
      });

      showToast({
        message: "Code downloaded successfully",
        type: "success",
      });

      try {
        // Clean up the URL object. Deferred to ensure the download has been triggered
        setTimeout(() => {
          // Free up memory
          URL.revokeObjectURL(url);
          // Remove the anchor
          document.body.removeChild(link);
        }, 0);
      } catch (_error) {
        // Do nothing if cleanup fails since it's not critical
      }
    } catch (error) {
      handleErrorWithToast({
        id: `getToken-${Date.now().toString()}`,
        title: "Failed to download the code. Please try again.",
        error,
      });

      trackEvent({
        name: WorkspaceEvents.finishDownloadCode,
        props: {
          isSuccess: false,
        },
      });
    }

    setIsLoading(false);

    // For some reason trackEvent triggers infinite re-renders if added to
    // the dependencies array. That's why it has to be removed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, handleErrorWithToast, showToast]);

  return (
    <Button
      onClick={downloadCode}
      isLoading={isLoading}
    >
      Download code
    </Button>
  );
}
